import React from 'react';
import { View, Pressable, ViewStyle, StyleProp, LayoutChangeEvent } from 'react-native';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { useHover } from '@/hooks/common/useHover';
import { WEB_STYLES } from '@/utils/webStyles';

interface GlassCardProps {
  borderRadius?: number;
  dashed?: boolean;
  padding?: number;
  style?: StyleProp<ViewStyle>;
  interactive?: boolean;
  onPress?: () => void;
  onLayout?: (e: LayoutChangeEvent) => void;
  variant?: 'soft' | 'strong';
  children: React.ReactNode;
}

const InteractiveGlassCard: React.FC<GlassCardProps> = ({
  borderRadius = REDESIGN_SPACING.cardBorderRadius,
  dashed = false,
  padding = REDESIGN_SPACING.cardPadding,
  style,
  onPress,
  onLayout,
  variant = 'soft',
  children,
}) => {
  const { colors } = useThemeColors();
  const { isHovered, hoverProps } = useHover();
  const defaultBorder = dashed ? colors.card.borderDashed : colors.card.border;
  const hoveredBorder = colors.card.borderHover;
  const backgroundColor = variant === 'strong' ? colors.card.bgStrong : colors.card.bg;

  return (
    <Pressable
      onPress={onPress}
      onLayout={onLayout}
      style={[
        { backgroundColor, borderWidth: 1, overflow: 'hidden' as const },
        webCardStyle,
        {
          borderRadius,
          padding,
          borderStyle: dashed ? 'dashed' : 'solid',
          borderColor: isHovered ? hoveredBorder : defaultBorder,
        },
        style,
      ]}
      {...hoverProps}
    >
      {children}
    </Pressable>
  );
};

const StaticGlassCard: React.FC<Omit<GlassCardProps, 'interactive' | 'onPress'>> = ({
  borderRadius = REDESIGN_SPACING.cardBorderRadius,
  dashed = false,
  padding = REDESIGN_SPACING.cardPadding,
  style,
  onLayout,
  variant = 'soft',
  children,
}) => {
  const { colors } = useThemeColors();
  const backgroundColor = variant === 'strong' ? colors.card.bgStrong : colors.card.bg;

  return (
    <View
      onLayout={onLayout}
      style={[
        { backgroundColor, borderWidth: 1, overflow: 'hidden' as const },
        webCardStyle,
        {
          borderRadius,
          padding,
          borderStyle: dashed ? 'dashed' : 'solid',
          borderColor: dashed ? colors.card.borderDashed : colors.card.border,
        },
        style,
      ]}
    >
      {children}
    </View>
  );
};

export const GlassCard: React.FC<GlassCardProps> = React.memo(({
  interactive = false,
  ...props
}) => {
  if (interactive) {
    return <InteractiveGlassCard {...props} />;
  }
  return <StaticGlassCard {...props} />;
});

GlassCard.displayName = 'GlassCard';

const webCardStyle = {
  ...WEB_STYLES.backdropBlur(16),
  ...WEB_STYLES.transition('border-color', '0.15s', 'ease'),
} as any;
